import moment from 'moment'
import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { EuiFlexGroup, EuiFlexItem, EuiLink, EuiLoadingSpinner, EuiSpacer, EuiText, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { MasterCampaign } from 'api/interfaces/MasterCampaign'
import { useGetMasterCampaignsQuery } from 'api/rtkQueryApi/platform/masterCampaignsApi'

import { CampaignActiveStatus, CampaignStatus } from '../../../api/interfaces'
import { useGetCampaignsQuery } from '../../../api/rtkQueryApi/platform/campaignsApi'
import { RootState } from '../../../app/rootReducer'
import { AcEuiInMemoryTableWithSearch } from '../../../components/Basic/AcEuiInMemoryTableWithSearch'
import history from '../../../services/HistoryService'
import { getEarliestStartDate, getTotalMonthRange } from '../../../utils/CampaignMathUtils'

export const MasterCampaigns = () => {
  const [masterCampaigns, setMasterCampaigns] = React.useState<MasterCampaign[]>([])
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const masterCampaignsQuery = useGetMasterCampaignsQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount })
  const campaignsQuery = useGetCampaignsQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount })

  React.useEffect(() => {
    if (masterCampaignsQuery.data && campaignsQuery.data) {
      const campaigns = [...campaignsQuery.data]
      const masterCampaignsWithItems = masterCampaignsQuery.data.map(masterCampaign => {
        const items = campaigns.filter(c => c.masterCampaignId === masterCampaign.id)
        return {
          ...masterCampaign,
          campaigns: items
        }
      })
      setMasterCampaigns(masterCampaignsWithItems)
    }
  }, [masterCampaignsQuery.data, campaignsQuery.data])

  const location = useLocation()

  const onGroupClicked = (masterCampaign: MasterCampaign) => {
    history.push(`${location.pathname}/${masterCampaign.id}`)
  }

  const columns: EuiBasicTableColumn<MasterCampaign>[] = [
    {
      name: 'Master Campaign',
      field: 'name',
      sortable: true,
      render: (name: string, masterCampaign: MasterCampaign) => (
        <EuiLink onClick={() => onGroupClicked(masterCampaign)} color={'success'}>
          {name}
        </EuiLink>
      )
    },
    {
      name: 'Date Created',
      field: 'created',
      render: (created: string) => {
        return moment(created).format('MM/DD/YYYY')
      },
      sortable: (masterCampaign: MasterCampaign) => {
        return moment(masterCampaign.created).valueOf()
      }
    },
    {
      name: 'Start Date',
      render: (masterCampaign: MasterCampaign) => {
        return getEarliestStartDate(masterCampaign.campaigns)?.format('MM/DD/YYYY') ?? ''
      },
      sortable: (masterCampaign: MasterCampaign) => {
        return getEarliestStartDate(masterCampaign.campaigns)?.valueOf()
      }
    },
    {
      name: 'Number of Months',
      render: (masterCampaign: MasterCampaign) => {
        return getTotalMonthRange(masterCampaign.campaigns) ?? 1
      },
      sortable: (masterCampaign: MasterCampaign) => {
        return getTotalMonthRange(masterCampaign.campaigns) ?? 1
      }
    },
    {
      name: 'Active Campaigns',
      render: (masterCampaign: MasterCampaign) => {
        if (!campaignsQuery.data) {
          return <EuiLoadingSpinner size={'m'} />
        }
        return campaignsQuery.data.filter(c => c.masterCampaignId === masterCampaign.id && c.activeStatus === CampaignActiveStatus.Active).length
      }
    },
    {
      name: 'Upcoming Campaigns',
      render: (masterCampaign: MasterCampaign) => masterCampaign.campaigns.filter(c => ![CampaignStatus.Deleted, CampaignStatus.Archived, CampaignStatus.CampaignEnded].includes(c.status) && moment(c.startDate).isAfter(moment())).length
    },
    {
      name: 'Completed Campaigns',
      render: (masterCampaign: MasterCampaign) => {
        if (!campaignsQuery.data) {
          return <EuiLoadingSpinner size={'m'} />
        }
        return campaignsQuery.data.filter(c => c.masterCampaignId === masterCampaign.id && c.status === CampaignStatus.CampaignEnded).length
      }
    }
  ]

  const sorting = {
    sort: {
      field: 'created',
      direction: SortDirection.DESC
    }
  }

  const isLoading = masterCampaignsQuery.isFetching || campaignsQuery.isFetching

  return (
    <React.Fragment>
      <AcEuiInMemoryTableWithSearch
        searchFields={['name']}
        message={masterCampaignsQuery.isLoading ? 'Loading master campaigns...' : 'Master campaigns have not been created yet.'}
        search={{
          toolsRight: []
        }}
        loading={isLoading}
        items={masterCampaigns}
        columns={columns}
        sorting={sorting}
      />
      {(masterCampaigns.length ?? 0) <= 0 && (
        <EuiFlexGroup>
          <EuiFlexItem />
          <EuiFlexItem grow={false}>
            <EuiSpacer size='xxl' />
            <EuiTitle size='xs'>
              <h3 style={{ textAlign: 'center' }}>Master Campaigns are tied to the comprehensive marketing plans.</h3>
            </EuiTitle>

            <EuiSpacer size='s' />

            <EuiText style={{ textAlign: 'center' }}>
              To get started, click on <EuiLink href={'/ai'}>AI Planner</EuiLink> in the Left Navigation panel.
            </EuiText>

            <EuiSpacer size='xxl' />
          </EuiFlexItem>
          <EuiFlexItem />
        </EuiFlexGroup>
      )}
    </React.Fragment>
  )
}
